const POLARIS_SRC = 'https://widget.reviews.io/polaris/build.js';

/**
 * Charge le bundle Polaris de REVIEWS.io une seule fois (promesse partagée).
 * @returns {Promise<void>}
 */
const loadPolaris = () => {
	// NB : on teste `=== 'function'` et pas juste la présence. Un `<div id="ReviewsWidget">`
	// crée un global implicite `window.ReviewsWidget` (= l'élément) qui masquerait le
	// constructeur Polaris tant que build.js n'est pas chargé.
	if (typeof (/** @type {any} */ (window).ReviewsWidget) === 'function') return Promise.resolve();
	if (/** @type {any} */ (window).__reviewsIoPolaris) return /** @type {any} */ (window).__reviewsIoPolaris;

	const promise = new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = POLARIS_SRC;
		script.async = true;
		script.addEventListener('load', () => resolve(), { once: true });
		script.addEventListener('error', reject, { once: true });
		document.head.appendChild(script);
	});

	/** @type {any} */ (window).__reviewsIoPolaris = promise;
	return promise;
};

/**
 * Conteneur autonome du widget d'avis REVIEWS.io (Polaris).
 * Hydratation lazy : le widget vit dans un `<details>` fermé (Polaris ne peut pas
 * le mesurer tant qu'il est caché), donc on initialise au premier affichage —
 * ouverture de l'accordéon parent, ou entrée dans le viewport si pas de `<details>`.
 */
class ReviewsIoWidget extends HTMLElement {
	#hydrated = false;
	/** @type {HTMLDetailsElement | null} */
	#details = null;
	/** @type {IntersectionObserver | null} */
	#observer = null;

	connectedCallback() {
		this.#details = this.closest('details');

		if (this.#details) {
			if (this.#details.open) {
				this.#hydrate();
				return;
			}
			this.#details.addEventListener('toggle', this.#onToggle);
			return;
		}

		this.#observer = new IntersectionObserver((entries) => {
			if (entries.some((entry) => entry.isIntersecting)) this.#hydrate();
		});
		this.#observer.observe(this);
	}

	disconnectedCallback() {
		this.#details?.removeEventListener('toggle', this.#onToggle);
		this.#observer?.disconnect();
	}

	#onToggle = () => {
		if (this.#details?.open) this.#hydrate();
	};

	async #hydrate() {
		if (this.#hydrated) return;
		this.#hydrated = true;

		this.#observer?.disconnect();
		this.#details?.removeEventListener('toggle', this.#onToggle);

		if (this.dataset.mode === 'raw') {
			await this.#hydrateRaw();
			return;
		}

		const selector = this.dataset.selector;
		const configEl = this.querySelector('.reviews-io-widget__config');
		if (!selector || !configEl) return;

		let config;
		try {
			config = JSON.parse(configEl.textContent ?? '{}');
		} catch (error) {
			console.error('[reviews-io-widget] config JSON invalide', error);
			return;
		}

		try {
			await loadPolaris();
			new (/** @type {any} */ (window).ReviewsWidget)(selector, config);
		} catch (error) {
			console.error('[reviews-io-widget] hydratation échouée', error);
		}
	}

	/**
	 * Mode « raw » : le code REVIEWS.io collé par le client (réglage theme) est rendu par
	 * le snippet avec son init différé en `window.__reviewsIoInit` et son loader build.js
	 * retiré. On charge Polaris nous-mêmes puis on déclenche l'init, une fois l'onglet
	 * visible (Polaris mesure correctement).
	 */
	async #hydrateRaw() {
		try {
			await loadPolaris();
			/** @type {any} */ (window).__reviewsIoInit?.();
		} catch (error) {
			console.error('[reviews-io-widget] init (raw) échouée', error);
		}
	}
}

if (!customElements.get('reviews-io-widget')) {
	customElements.define('reviews-io-widget', ReviewsIoWidget);
}
